import { Injectable } from '@angular/core';
import { combineLatest, Observable} from 'rxjs';
import { SortOption } from '../interfaces/sort-option.interface';
import { filter, map, shareReplay } from 'rxjs/operators';
import { SellSortOptions } from '../data/sell-sort-options';
import { Select } from '@ngxs/store';
import { AuctionItemSearchPayload, LaunchDarklyService, StatusFilter, SellSearchState, defaultSortForStatus, sellSortFromParams } from 'remarketing-angular-library';
import { selectHasMembers } from './lead-seller-group/reducer';
import { LeadSellerGroupService } from './lead-seller-group/lead-seller-group.service';

@Injectable({
  providedIn: 'root'
})
export class SellSortService {
  @Select(SellSearchState.searchRequest) readonly searchRequest$!: Observable<AuctionItemSearchPayload>;

  hasLeadSellerMembers$: Observable<boolean> = this.leadSellerGroupService.select(selectHasMembers);

  constructor(
    readonly leadSellerGroupService: LeadSellerGroupService,
    readonly launchDarklyService: LaunchDarklyService,
    private readonly sellSortOptions: SellSortOptions
  ) {}

  readonly sortOptions$: Observable<SortOption[]> = combineLatest(this.hasLeadSellerMembers$, this.searchRequest$).pipe(
    filter(request => !!request[1]),
    map(([hasLeadMemberSellers, request]) => {
      let sortOptions;
      switch (request.filterByStatus) {
      case StatusFilter.SOLD_VEHICLES:
        sortOptions = this.sellSortOptions.sold();
        break;
      case StatusFilter.HOLD_VEHICLES:
        sortOptions = this.sellSortOptions.hold();
        break;
      case StatusFilter.REMOVED_VEHICLES:
        sortOptions = this.sellSortOptions.removed();
        break;
      case null:
        sortOptions = this.sellSortOptions.all();
        break;
      case StatusFilter.ACTIVE_VEHICLES:
      default:
        sortOptions = this.sellSortOptions.active();
        break;
      }
      sortOptions = this.handleSortPermissions(sortOptions, hasLeadMemberSellers);
      return this.mapToSelected(sortOptions, request);
    }),
    shareReplay(1)
  );

  private mapToSelected(options: SortOption[], request: AuctionItemSearchPayload): SortOption[] {
    let hasSelected = false;
    const { sortBy = '', sortOrder = '' } = request || {};
    let sortValue = sellSortFromParams(sortBy, sortOrder);
    if (typeof sortValue === 'undefined') {
      sortValue = defaultSortForStatus(request.filterByStatus as StatusFilter);
    }
    const mappedOptions = options.map(o => {
      return {
        header: o.header,
        options: o.options.map(o2 => {
          const selected = o2.value === sortValue;
          if (selected) {
            hasSelected = true;
          }
          return { ...o2, selected };
        })
      };
    });
    if (!hasSelected) {
      // This will always be called with an array at least this long
      mappedOptions[0].options[0].selected = true;
    }
    return mappedOptions;
  }

  private handleSortPermissions(sortOptions: SortOption[], hasLeadMemberSellers: boolean) {
    let sortedOptions = [...sortOptions];

    // Remove Lead Seller options if the permission is not present
    if (!hasLeadMemberSellers) {
      sortedOptions = sortedOptions.filter(x => !x.requiresLeadSeller);
    }

    // Remove SellUpdateInventory options if permission is not present
    if(!this.isSellUpdateInventoryFeatureEnabled) {
      sortedOptions = sortedOptions.filter(x => !x.requiresSellUpdateInventory);
    }

    // Remove TenantBadge options if flag is not enabled
    if(!this.isTenantBadgeEnabled) {
      sortedOptions = sortedOptions.filter(x => !x.requiresSellTenantBadge);
    }

    return sortedOptions;
  }

  get isSellUpdateInventoryFeatureEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-updateInventoryEnabled', false);
  }

  get isTenantBadgeEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-tenantBadgeEnabled', false);
  }
}
